import { useState } from 'react';
import { useParams } from 'react-router-dom';

import ChevronLeftIcon from '@/app/assets/svg/chevron-left.svg?react';
import InfoIcon from '@/app/assets/svg/info.svg?react';
import { useVaultPerformance } from '@/app/components/VaultsItemPageComponents/hooks/useVaultPerformance';
import MarketDetailsMeta from '@/app/components/VaultsItemPageComponents/MarketDetailsMeta';
import MarketDetailsPositions from '@/app/components/VaultsItemPageComponents/MarketDetailsPositions';
import MarketDetailsTrancheInfo from '@/app/components/VaultsItemPageComponents/MarketDetailsTrancheInfo';
import VaultsItemActionCard from '@/app/components/VaultsItemPageComponents/VaultsItemActionCard';
import VaultsItemPageContainer from '@/app/components/VaultsItemPageComponents/VaultsItemPageContainer';
import VaultsItemPerformance from '@/app/components/VaultsItemPageComponents/VaultsItemPerformance';
import { useVaultById } from '@/app/components/VaultsPageComponents/hooks/useVaults';
import VaultAvatar from '@/app/components/VaultsPageComponents/VaultAvatar';
import VaultInfoModal from '@/app/components/VaultsPageComponents/VaultInfoModal';
import { NotFoundPage } from '@/app/pages/NotFoundPage/NotFoundPage';
import { commonRoutes } from '@/app/providers/router/routes';
import { DASH } from '@/app/shared/constants/common';
import { getVaultDescription, VAULT_TRANCHE, VaultTrancheId } from '@/app/shared/constants/vaults';
import { formatVaultApy, formatVaultPercent, formatVaultTvl } from '@/app/shared/lib/vaultFormatters';
import { getCurrentPerformanceTvl, getLatestChartTvl } from '@/app/shared/lib/vaultPerformance';
import { AppLink } from '@/app/shared/ui/AppLink';
import { Button } from '@/app/shared/ui/Button';
import { PerformanceChartData, PerformanceChartPeriod } from '@/app/shared/ui/PerformanceChart/types';

const MarketDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { vault, name: vaultName, isLoading } = useVaultById(id);
  const marketName = vaultName || DASH;
  const description = getVaultDescription(id);

  const [selectedTrancheId, setSelectedTrancheId] = useState<VaultTrancheId>(VAULT_TRANCHE.JUNIOR);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const { points: tvlPoints, data: performanceData } = useVaultPerformance({
    vaultId: vault ? id : undefined,
    period: PerformanceChartPeriod.WEEK,
    metric: PerformanceChartData.TVL,
  });
  const latestTvl =
    getCurrentPerformanceTvl(performanceData?.tranches ?? []) ?? getLatestChartTvl(tvlPoints);

  const marketTvlRaw = vault?.tvlUsd ?? vault?.tvl;
  const marketTvl =
    latestTvl != null
      ? formatVaultTvl(String(latestTvl.total))
      : vault
        ? formatVaultTvl(marketTvlRaw)
        : DASH;
  const seniorTvl =
    latestTvl?.senior != null ? formatVaultTvl(String(latestTvl.senior)) : DASH;
  const juniorTvl =
    latestTvl?.junior != null ? formatVaultTvl(String(latestTvl.junior)) : DASH;
  const baseApy = vault ? formatVaultApy(vault.baseApy) : DASH;
  const seniorApy = vault ? formatVaultApy(vault.trancheSeniorAPY) : DASH;
  const juniorApy = vault ? formatVaultApy(vault.trancheJuniorAPY) : DASH;
  const coverage = vault ? formatVaultPercent(vault.trancheJuniorPercentage) : DASH;

  if (!id || (!isLoading && !vault)) {
    return <NotFoundPage />;
  }

  if (isLoading) {
    return null;
  }

  return (
    <>
      <VaultsItemPageContainer
        hero={
          <div className='flex h-full flex-col justify-end px-5 pt-10 pb-10 xl:px-10'>
            <div className='flex items-center justify-between gap-4'>
              <AppLink
                to={commonRoutes.VAULTS}
                className='flex w-fit items-center gap-2 rounded-md bg-gray-800 px-2 py-1 text-sm text-gray-400 transition-colors duration-200 ease-out hover:bg-elevated-card-hover hover:text-gray-300'
              >
                <ChevronLeftIcon className='h-4 w-4' />
                Back
              </AppLink>
              <div className='hidden lg:block'>
                <MarketDetailsMeta />
              </div>
            </div>
            <div className='mt-11 flex flex-col gap-3'>
              <div className='flex items-center gap-3'>
                <VaultAvatar vaultId={id} name={marketName} />
                <div className='flex min-w-0 items-center gap-1.5'>
                  <h1 className='text-[30px] font-normal text-brand-1100'>{marketName}</h1>
                  <Button
                    type='button'
                    variant='ghost'
                    aria-label={`${marketName} vault information`}
                    className='h-auto shrink-0 rounded-none p-0 text-gray-600 hover:bg-transparent hover:text-gray-400'
                    onClick={() => setIsInfoOpen(true)}
                  >
                    <InfoIcon className='h-4 w-4' />
                  </Button>
                </div>
              </div>
              {description && (
                <div className='flex w-full max-w-160 flex-col gap-1'>
                  {description.split(/\n+/).map((paragraph) => (
                    <p key={paragraph.slice(0, 24)} className='text-base leading-6 text-gray-300'>
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}
              <div className='pt-3 lg:hidden'>
                <MarketDetailsMeta />
              </div>
            </div>
          </div>
        }
        stats={[
          {
            label: 'Market TVL',
            secondary: baseApy === DASH ? undefined : { label: 'Base APY', value: baseApy },
            value: marketTvl,
          },
          {
            label: 'Senior TVL',
            secondary: seniorApy === DASH ? undefined : { label: 'APY', value: seniorApy },
            value: seniorTvl,
          },
          {
            label: 'Junior TVL',
            secondary: juniorApy === DASH ? undefined : { label: 'APY', value: juniorApy },
            value: juniorTvl,
          },
          {
            label: 'Coverage',
            value: coverage,
          },
        ]}
      >
        <div className='flex flex-col'>
          <div className='flex flex-col gap-2'>
            <MarketDetailsTrancheInfo trancheId={selectedTrancheId} />
            <div className='grid grid-cols-1 items-stretch gap-2 min-[1056px]:grid-cols-[minmax(0,1fr)_auto]'>
              <div className='order-2 min-w-0 min-[1056px]:order-1'>
                <VaultsItemPerformance key={`${id}-performance`} vaultId={id} />
              </div>
              <div className='order-1 min-w-0 min-[1056px]:order-2'>
                <VaultsItemActionCard
                  key={`${id}-action`}
                  vaultId={id}
                  selectedTrancheId={selectedTrancheId}
                  onTrancheChange={setSelectedTrancheId}
                />
              </div>
            </div>
          </div>

          <div className='mt-10'>
            <MarketDetailsPositions />
          </div>
        </div>
      </VaultsItemPageContainer>

      <VaultInfoModal
        isOpen={isInfoOpen}
        vaultId={id ?? null}
        vaultName={marketName === DASH ? null : marketName}
        onClose={() => setIsInfoOpen(false)}
      />
    </>
  );
};

export default MarketDetailsPage;
